/**
 * Tag canonicalisation for cookbook views.
 *
 * The problem: over time a cookbook accumulates dozens of near-duplicate
 * tags — 'Dessert' vs 'desserts' vs 'Desserts', 'gluten-free' vs
 * 'Gluten Free' vs 'GlutenFree', 'Family Favourite' vs 'family favourites'.
 * The filter chip row balloons and users can't tell them apart.
 *
 * This module collapses variants at DISPLAY time (no DB writes) using a
 * signature that ignores case, punctuation and simple English plurals.
 * The most common display casing wins as the label — ties broken by the
 * shortest string, then alphabetical, to keep it predictable.
 */

/** Aggressive signature for grouping variants of the same tag. */
export function canonicalTagSignature(tag) {
  if (!tag) return "";
  let s = String(tag).toLowerCase();
  // Strip everything but a-z and digits so "gluten-free" == "gluten free" == "glutenfree"
  s = s.replace(/[^a-z0-9]+/g, "");
  if (!s) return "";
  // Simple English plural collapse:
  //   → 'berries' → 'berry'
  //   → 'boxes'   → 'box'
  //   → 'classes' → 'class' (leaves 'ss' alone)
  //   → 'cakes'   → 'cake'
  //   → 'kids'    → 'kid'
  // Rules stay conservative to avoid over-collapsing (e.g. 'gas' → 'ga').
  if (s.length > 4 && s.endsWith("ies")) {
    s = s.slice(0, -3) + "y";
  } else if (s.length > 4 && s.endsWith("sses")) {
    s = s.slice(0, -2);
  } else if (s.length > 4 && s.endsWith("es") && /(?:[sxz]|ch|sh)$/.test(s.slice(0, -2))) {
    s = s.slice(0, -2);
  } else if (s.length > 3 && s.endsWith("s") && !s.endsWith("ss") && !s.endsWith("us")) {
    s = s.slice(0, -1);
  }
  return s;
}

/**
 * Group an array of `{ tag, count }` (or plain string tags) into
 * canonicalised buckets. Returns `[{ label, count, variants }]` sorted
 * by count descending, then alphabetical.
 *
 * `variants` is the full list of original strings that mapped to this
 * bucket — used for filtering (a click on the "Desserts" chip should
 * still match recipes tagged 'dessert' or 'DESSERT').
 */
export function groupTags(rawTags) {
  // Normalise input to [{ tag, count }].
  const entries = Array.isArray(rawTags)
    ? rawTags.map((t) => (typeof t === "string" ? { tag: t, count: 1 } : t))
    : [];
  const buckets = new Map();
  for (const { tag, count } of entries) {
    const sig = canonicalTagSignature(tag);
    if (!sig) continue;
    if (!buckets.has(sig)) {
      buckets.set(sig, { sig, count: 0, variantCounts: new Map() });
    }
    const b = buckets.get(sig);
    b.count += count || 0;
    b.variantCounts.set(tag, (b.variantCounts.get(tag) || 0) + (count || 0));
  }
  const groups = [];
  for (const b of buckets.values()) {
    // Pick the "best" label: highest count → shortest → alphabetical.
    const sortedVariants = [...b.variantCounts.entries()].sort((a, z) => {
      if (z[1] !== a[1]) return z[1] - a[1];
      if (a[0].length !== z[0].length) return a[0].length - z[0].length;
      return a[0].localeCompare(z[0]);
    });
    groups.push({
      label: sortedVariants[0][0],
      count: b.count,
      variants: sortedVariants.map((v) => v[0]),
      signature: b.sig,
    });
  }
  groups.sort((a, z) => {
    if (z.count !== a.count) return z.count - a.count;
    return a.label.localeCompare(z.label);
  });
  return groups;
}
